import { t as translate } from "./preferences";
import { useCallback, useEffect, useRef, useState } from "react";
import { haptic } from "./haptic";
import { useAuth } from "./auth";
import { messageOf } from "./components/DataState";
import type { WarehouseOperationKind, WarehouseWarningOut } from "./api";
import {
  readWarehouseOperation,
  settleWarehouseOperation,
  writeWarehouseOperation,
  type PendingWarehouseOperation,
} from "./warehouseOperation";

export function useWarehouseOperation(
  kind: WarehouseOperationKind,
  resourceId: number | undefined,
  onSuccess: (result: WarehouseWarningOut) => void,
  onError?: (error: unknown) => void,
) {
  const { state, access } = useAuth();
  if (state.status !== "authenticated" || state.user.workshop_id == null) {
    throw new Error("Warehouse operation requires an authenticated workshop user");
  }
  const user = state.user;
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [unknown, setUnknown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocking, setBlocking] = useState(() => readWarehouseOperation(user.id, user.workshop_id!));
  const recovered = useRef(false);
  const successRef = useRef(onSuccess);
  const errorRef = useRef(onError);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  successRef.current = onSuccess;
  errorRef.current = onError;

  const run = useCallback(async (operation: PendingWarehouseOperation, allowReplay: boolean, resume = false) => {
    if (savingRef.current) return;
    savingRef.current = true; setSaving(true); setError(null);
    const outcome = await settleWarehouseOperation(operation, { allowReplay, resume });
    if (!mounted.current) return;
    savingRef.current = false; setSaving(false);
    if (outcome.status === "success") {
      setUnknown(false); setBlocking(null); haptic("success"); successRef.current(outcome.result);
    } else if (outcome.status === "unknown") {
      setUnknown(true); setBlocking(outcome.operation); haptic("warning");
      setError(translate("Natija noma’lum. Operatsiya raqami saqlandi; yangi amal boshlanmaydi. Sessiyani tiklab xavfsiz tekshiring."));
    } else {
      setUnknown(false); setBlocking(null); haptic("error"); setError(messageOf(outcome.error));
      errorRef.current?.(outcome.error);
    }
  }, []);

  useEffect(() => {
    if (recovered.current || !blocking) return;
    if (blocking.kind !== kind || blocking.resourceId !== resourceId) return;
    recovered.current = true;
    void run(blocking, false, true);
  }, [blocking, kind, resourceId, run]);

  async function submit(payload: Record<string, unknown>, operationUuid: string) {
    if (savingRef.current) return;
    const existing = readWarehouseOperation(user.id, user.workshop_id!);
    if (existing) {
      setBlocking(existing);
      if (existing.kind === kind && existing.resourceId === resourceId) void run(existing, false, true);
      else setError(translate("Oldingi ombor operatsiyasi yakunlanmagan. Avval o‘sha natijani tekshiring."));
      return;
    }
    const operation: PendingWarehouseOperation = {
      version: 1,
      userId: user.id,
      workshopId: user.workshop_id!,
      kind,
      operationUuid,
      payload,
      resourceId,
      createdAt: Date.now(),
      replayUsed: false,
      outcomeUnknown: false,
    };
    if (!writeWarehouseOperation(operation)) {
      setError(translate("Operatsiya raqamini xavfsiz saqlab bo‘lmadi. Brauzer xotirasiga ruxsat bering."));
      return;
    }
    setBlocking(operation);
    await run(operation, true);
  }

  function retry() {
    if (access?.checking || access?.recovery || access?.error) return;
    const operation = readWarehouseOperation(user.id, user.workshop_id!);
    if (operation?.kind === kind && operation.resourceId === resourceId) void run(operation, false, true);
  }
  return { submit, retry, saving, unknown, error, setError, blocking };
}
